import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { PageLayout } from '@/components/layout';
import RecipeCollectionComponent from '@/components/RecipeCollection';
import AddRecipeActionSheet from '@/components/AddRecipeActionSheet';
import FAB from '@/components/ui/fab';
import { useIsMobile } from '@/hooks/use-mobile';

interface RecipeCollectionProps {
  isNewUser?: boolean;
}

const RecipeCollection: React.FC<RecipeCollectionProps> = ({ isNewUser = false }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // State for action sheet and tag selection
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleTagRemove = (tag: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  const handleClearAllTags = () => {
    setSelectedTags([]);
  };

  const handleCompositionEdit = (composition: any) => {
    navigate(`/composition/${composition.id}/edit`);
  };

  // Desktop add recipe button
  const addRecipeButton = (
    <Button 
      onClick={() => setIsActionSheetOpen(true)}
      className="hidden md:flex items-center gap-2"
      variant="default"
    >
      <Plus className="h-4 w-4" />
      Add Recipe
    </Button>
  );

  return (
    <>
      <PageLayout
        title="Recipe Collection"
        description="Browse your recipes and meal compositions with smart filtering and organization"
        headerRight={addRecipeButton}
      >
        <div className="pb-8">
          <RecipeCollectionComponent 
            onCompositionEdit={handleCompositionEdit}
            selectedTags={selectedTags}
            onTagSelect={handleTagSelect}
            onTagRemove={handleTagRemove}
            onClearAllTags={handleClearAllTags}
          />
        </div>
      </PageLayout>

      {/* Mobile FAB */}
      <FAB 
        onClick={() => setIsActionSheetOpen(true)}
        className="md:hidden"
      />

      {/* Add Recipe Action Sheet */}
      <AddRecipeActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
      />
    </>
  );
};

export default RecipeCollection;